import { type QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, call } from "./client";
import type {
  AuthState,
  BookingView,
  CharacterListItem,
  CharacterView,
  LogEntryView,
  OpenCharacterView,
  MapImageView,
  Operation,
  PackageListView,
  PlacementTargetView,
  PreviewView,
  SystemInfo,
  TransitionView,
  UserView,
} from "./types";

export const keys = {
  auth: ["auth"] as const,
  systems: ["systems"] as const,
  characters: ["characters"] as const,
  openCharacters: ["open-characters"] as const,
  character: (id: string) => ["character", id] as const,
  log: (id: string) => ["character", id, "log"] as const,
  bookings: (id: string) => ["character", id, "bookings"] as const,
  transition: (id: string) => ["character", id, "transition"] as const,
  users: ["users"] as const,
  connectedApps: ["connected-apps"] as const,
  allowedApps: ["allowed-apps"] as const,
  packages: ["packages"] as const,
  maps: (system: string) => ["maps", system] as const,
};

export function useAuth() {
  return useQuery({ queryKey: keys.auth, queryFn: () => call<AuthState>(api.GET("/api/auth/state")) });
}

export function useSystems(enabled = true) {
  return useQuery({ queryKey: keys.systems, queryFn: () => call<SystemInfo[]>(api.GET("/api/systems")), enabled, staleTime: 60_000 });
}

export function useCharacters() {
  return useQuery({ queryKey: keys.characters, queryFn: () => call<CharacterListItem[]>(api.GET("/api/characters")) });
}

/** A character; with refreshEvery (ms) it is fetched again at that interval while the page is visible, and when the window regains focus. */
export function useCharacterQuery(id: string, refreshEvery?: number) {
  return useQuery({
    queryKey: keys.character(id),
    queryFn: () => call<CharacterView>(api.GET("/api/characters/{id}", { params: { path: { id } } })),
    ...(refreshEvery ? { refetchInterval: refreshEvery, refetchOnWindowFocus: true } : {}),
  });
}

export function useLog(id: string) {
  return useQuery({
    queryKey: keys.log(id),
    queryFn: () => call<LogEntryView[]>(api.GET("/api/characters/{id}/log", { params: { path: { id } } })),
  });
}

export function useBookings(id: string) {
  return useQuery({
    queryKey: keys.bookings(id),
    queryFn: () => call<BookingView[]>(api.GET("/api/characters/{id}/bookings", { params: { path: { id } } })),
  });
}

export function useTransitionCheck(id: string, enabled: boolean) {
  return useQuery({
    queryKey: keys.transition(id),
    queryFn: () => call<TransitionView>(api.GET("/api/characters/{id}/transition", { params: { path: { id } } })),
    enabled,
  });
}

export function useUsers(enabled = true) {
  return useQuery({ queryKey: keys.users, queryFn: () => call<UserView[]>(api.GET("/api/users")), enabled });
}

export function usePackages() {
  return useQuery({ queryKey: keys.packages, queryFn: () => call<PackageListView>(api.GET("/api/packages")) });
}

/** Stores a fresh character view and drops everything derived from the old one. */
export function useCharacterUpdate(id: string) {
  const client = useQueryClient();
  return (view: CharacterView) => {
    client.setQueryData(keys.character(id), view);
    void client.invalidateQueries({ queryKey: keys.log(id) });
    void client.invalidateQueries({ queryKey: keys.bookings(id) });
    void client.invalidateQueries({ queryKey: keys.transition(id) });
    void client.invalidateQueries({ queryKey: keys.characters });
    refreshOpenCharacters(client);
  };
}

export function useApply(id: string) {
  const update = useCharacterUpdate(id);
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ operation, reportChanges = false }: { operation: Operation; reportChanges?: boolean }) => {
      const current = client.getQueryData<CharacterView>(keys.character(id));
      return call<CharacterView>(
        api.POST("/api/characters/{id}/operations", {
          params: { path: { id } },
          body: { operation: { type: operation.type, payload: operation.payload ?? {} }, expectedRevision: current?.revision ?? null, reportChanges },
        }),
      );
    },
    onSuccess: update,
  });
}

export function previewOperation(id: string, operation: Operation) {
  return call<PreviewView>(
    api.POST("/api/characters/{id}/operations/preview", {
      params: { path: { id } },
      body: { type: operation.type, payload: operation.payload ?? {} },
    }),
  );
}

/** Every target of „move to …“ for the given things, with the reason a target is blocked; judged by the server only. */
export function placementTargets(id: string, ids: string[]) {
  return call<PlacementTargetView[]>(api.POST("/api/characters/{id}/placement/targets", { params: { path: { id } }, body: { ids } }));
}

export function useUndoRedo(id: string) {
  const update = useCharacterUpdate(id);
  const undo = useMutation({
    mutationFn: () => call<CharacterView>(api.POST("/api/characters/{id}/undo", { params: { path: { id } } })),
    onSuccess: update,
  });
  const redo = useMutation({
    mutationFn: () => call<CharacterView>(api.POST("/api/characters/{id}/redo", { params: { path: { id } } })),
    onSuccess: update,
  });
  return { undo, redo };
}

export function useOpenCharacters() {
  return useQuery({ queryKey: keys.openCharacters, queryFn: () => call<OpenCharacterView[]>(api.GET("/api/open-characters")) });
}

/**
 * Fetches the open characters again, unless changes to the list are under way: the last of them fetches the list
 * once it has settled, and a fetch now could briefly bring back the state before a change already shown.
 */
export function refreshOpenCharacters(client: QueryClient) {
  if (client.isMutating({ mutationKey: keys.openCharacters }) === 0) void client.invalidateQueries({ queryKey: keys.openCharacters });
}

/**
 * Only the request sent on the way out outlives the page (keepalive). The others end with it, so one still waiting its
 * turn cannot arrive after that request, or after the next page has sent the page again.
 */
function rememberPage(id: string, lastPath?: string, keepalive = false) {
  return call<OpenCharacterView[]>(
    api.PUT("/api/open-characters/{id}", { params: { path: { id } }, body: { lastPath: lastPath ?? null }, keepalive }),
  );
}

const leftPageKey = "splittertraeger.left-page";
const leftPageFresh = 60_000;

interface LeftPage {
  id: string;
  lastPath: string;
  at: number;
}

/**
 * Remembers the page while the page itself is being left, bypassing changes still waiting their turn. The request may
 * still be under way, or overtaken by an older one, when the next page reads the list; so the page is also kept in this
 * browser tab for {@link takeLeftPage}.
 */
export function rememberPageOnExit(id: string, lastPath: string) {
  try {
    sessionStorage.setItem(leftPageKey, JSON.stringify({ id, lastPath, at: Date.now() } satisfies LeftPage));
  } catch {
    // Without storage only the request remembers the page.
  }
  void rememberPage(id, lastPath, true).catch(() => undefined);
}

/** The page remembered when this tab last left the app, once and only shortly after. */
export function takeLeftPage(): { id: string; lastPath: string } | null {
  try {
    const left = JSON.parse(sessionStorage.getItem(leftPageKey) ?? "null") as LeftPage | null;
    sessionStorage.removeItem(leftPageKey);
    return left && Date.now() - left.at < leftPageFresh ? { id: left.id, lastPath: left.lastPath } : null;
  } catch {
    return null;
  }
}

/**
 * Changes to the list of open characters. Each change shows at once in the cache and is sent after the previous
 * one has been answered, so the server sees them in the order they were made. The list is fetched again
 * once the last of several quick changes has settled, so a late answer cannot undo a newer change.
 */
export function useOpenCharacterActions() {
  const client = useQueryClient();
  const mutationKey = keys.openCharacters;
  const scope = { id: "open-characters" };

  const optimistic = async (change: (list: OpenCharacterView[]) => OpenCharacterView[]) => {
    await client.cancelQueries({ queryKey: keys.openCharacters });
    client.setQueryData<OpenCharacterView[]>(keys.openCharacters, (list) => (list ? change(list) : list));
  };
  const settle = () => {
    if (client.isMutating({ mutationKey }) === 1) void client.invalidateQueries({ queryKey: keys.openCharacters });
  };

  const open = useMutation({
    mutationKey,
    scope,
    mutationFn: ({ id, lastPath }: { id: string; lastPath?: string }) => rememberPage(id, lastPath),
    onMutate: ({ id, lastPath }) => optimistic((list) => list.map((c) => (c.id === id && lastPath ? { ...c, lastPath } : c))),
    // A character opened now is in the list at once, not only after the changes behind it have settled.
    onSuccess: (result, { id }) =>
      client.setQueryData<OpenCharacterView[]>(keys.openCharacters, (list) => {
        const added = result.find((c) => c.id === id);
        return list && added && !list.some((c) => c.id === id) ? [...list, added] : list;
      }),
    onSettled: settle,
  });
  const close = useMutation({
    mutationKey,
    scope,
    // Closing and reordering are the user's own steps and are carried out even when the page is left right after them.
    mutationFn: (id: string) => call<OpenCharacterView[]>(api.DELETE("/api/open-characters/{id}", { params: { path: { id } }, keepalive: true })),
    onMutate: (id) => optimistic((list) => list.filter((c) => c.id !== id)),
    onSettled: settle,
  });
  const reorder = useMutation({
    mutationKey,
    scope,
    mutationFn: (ids: string[]) => call<OpenCharacterView[]>(api.PUT("/api/open-characters", { body: { ids }, keepalive: true })),
    onMutate: (ids) => optimistic((list) => ids.flatMap((id) => list.filter((c) => c.id === id))),
    onSettled: settle,
  });
  return { open, close, reorder };
}

/** Map images of a system: which maps the rule data names and whether an admin has uploaded their image. */
export function useMapImages(system: string) {
  return useQuery({
    queryKey: keys.maps(system),
    queryFn: () => call<MapImageView[]>(api.GET("/api/systems/{system}/maps", { params: { path: { system } } })),
    staleTime: 60_000,
  });
}

export function mapImageUrl(system: string, map: MapImageView): string {
  const version = map.updatedAt ? `?v=${encodeURIComponent(map.updatedAt)}` : "";
  return `/api/systems/${encodeURIComponent(system)}/maps/${encodeURIComponent(map.id)}/image${version}`;
}
